import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { PersonAvatarEditable } from "@/components/persons/PersonAvatarEditable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TimeSelect } from "@/components/ui/time-select";
import { type PersonDetail, useUpdateOwnContact } from "@/hooks/use-persons";
import { useWorkLocationOptions } from "@/hooks/use-work-locations";
import { type PersonSelfContactInput, personSelfContactSchema } from "@/schemas/persons";

// Radix Select disallows "" values — sentinel for the "no location" row.
const LOCATION_NONE = "__none__";

interface PersonSelfContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: PersonDetail;
}

/**
 * Self-service editor for a person's OWN contact info: personal email, phone,
 * location, working hours, and profile photo. Applies immediately (no
 * effective-date step) and notifies HR server-side. Rendered only on the
 * signed-in user's own profile.
 */
export function PersonSelfContactDialog({
  open,
  onOpenChange,
  person,
}: PersonSelfContactDialogProps) {
  const update = useUpdateOwnContact(person.id);
  // Work location is admin-managed config, so the options come from the table
  // (active only). The person's own current value is appended when it has since
  // been retired — otherwise saving this form would silently clear it.
  const locations = useWorkLocationOptions();
  const heldLocation = person.location;
  const locationOptions =
    !heldLocation || locations.options.some((o) => o.value === heldLocation)
      ? locations.options
      : [...locations.options, { value: heldLocation, label: locations.labelFor(heldLocation) }];
  const [showSecondShift, setShowSecondShift] = useState(
    Boolean(person.workingHoursStart2 || person.workingHoursEnd2),
  );

  const form = useForm<PersonSelfContactInput>({
    resolver: zodResolver(personSelfContactSchema),
    defaultValues: {
      personalEmail: person.personalEmail ?? "",
      phone: person.phone ?? "",
      location: person.location ?? null,
      workingHoursStart: person.workingHoursStart ?? "",
      workingHoursEnd: person.workingHoursEnd ?? "",
      workingHoursStart2: person.workingHoursStart2 ?? "",
      workingHoursEnd2: person.workingHoursEnd2 ?? "",
      workingHoursNote: person.workingHoursNote ?? "",
    },
  });

  function handleOpenChange(next: boolean) {
    if (!next) {
      form.reset();
      setShowSecondShift(Boolean(person.workingHoursStart2 || person.workingHoursEnd2));
    }
    onOpenChange(next);
  }

  function onSubmit(values: PersonSelfContactInput) {
    // Empty optional strings/times collapse to NULL server-side; send them as-is.
    update.mutate(
      {
        ...values,
        personalEmail: values.personalEmail?.trim() ? values.personalEmail : "",
        phone: values.phone?.trim() ? values.phone : "",
        workingHoursStart: values.workingHoursStart || null,
        workingHoursEnd: values.workingHoursEnd || null,
        workingHoursStart2: values.workingHoursStart2 || null,
        workingHoursEnd2: values.workingHoursEnd2 || null,
        workingHoursNote: values.workingHoursNote?.trim() ? values.workingHoursNote : "",
      },
      { onSuccess: () => handleOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Edit contact info</DialogTitle>
          <DialogDescription>
            Update your contact details. Changes apply right away and HR is notified.
          </DialogDescription>
        </DialogHeader>

        {/* Photo applies immediately on its own (via the avatar route), separate
            from the form submit. */}
        <div className="flex items-center gap-4">
          <PersonAvatarEditable person={person} />
          <p className="text-sm text-muted-foreground">
            Click the camera to change your profile photo.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="personalEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Personal email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="person@gmail.com"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="+1 555 0100" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <Select
                      value={field.value ?? LOCATION_NONE}
                      onValueChange={(v) => field.onChange(v === LOCATION_NONE ? null : v)}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={LOCATION_NONE}>
                          <span className="text-muted-foreground">Unspecified</span>
                        </SelectItem>
                        {locationOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-2">
              <p className="font-medium text-foreground text-sm">
                Working hours{" "}
                <span className="font-normal text-muted-foreground">(Eastern Time, ET)</span>
              </p>
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex w-60 flex-col gap-3">
                  <FormField
                    control={form.control}
                    name="workingHoursStart"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-normal text-muted-foreground">
                          Start
                        </FormLabel>
                        <TimeSelect
                          value={field.value ?? null}
                          onChange={field.onChange}
                          placeholder="Start"
                          aria-label="Start time (Eastern Time)"
                          aria-invalid={Boolean(fieldState.error)}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="workingHoursEnd"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-normal text-muted-foreground">
                          End
                        </FormLabel>
                        <TimeSelect
                          value={field.value ?? null}
                          onChange={field.onChange}
                          placeholder="End"
                          aria-label="End time (Eastern Time)"
                          aria-invalid={Boolean(fieldState.error)}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <label className="flex cursor-pointer items-center gap-2 self-center text-sm font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={showSecondShift}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (!checked) {
                        form.setValue("workingHoursStart2", "");
                        form.setValue("workingHoursEnd2", "");
                      }
                      setShowSecondShift(checked);
                    }}
                    className="h-4 w-4 cursor-pointer rounded border-input text-primary accent-current"
                  />
                  Split shift
                </label>

                {showSecondShift && (
                  <div className="flex w-60 flex-col gap-3">
                    <FormField
                      control={form.control}
                      name="workingHoursStart2"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-normal text-muted-foreground">
                            Start
                          </FormLabel>
                          <TimeSelect
                            value={field.value ?? null}
                            onChange={field.onChange}
                            placeholder="Start"
                            aria-label="Second-set start time (Eastern Time)"
                            aria-invalid={Boolean(fieldState.error)}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="workingHoursEnd2"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-normal text-muted-foreground">
                            End
                          </FormLabel>
                          <TimeSelect
                            value={field.value ?? null}
                            onChange={field.onChange}
                            placeholder="End"
                            aria-label="Second-set end time (Eastern Time)"
                            aria-invalid={Boolean(fieldState.error)}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="workingHoursNote"
                  render={({ field }) => (
                    <FormItem className="min-w-[16rem] flex-1">
                      <FormLabel className="text-xs font-normal text-muted-foreground">
                        Note
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="e.g. Mon–Thu, flexible Fridays"
                          className="min-h-[80px]"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
